import { AdminScreenKey } from '../../types/admin-roles';
import { GaragesNeedsAction, OrdersDashboard, QueueCount, RefundsDashboard, ReviewsDashboard } from '../../types/audit';
import BlockErrorState from './BlockErrorState';
import NeedsActionTile from './NeedsActionTile';
import { DashboardBlock } from './useDashboardBlock';

type Source = 'garages' | 'orders' | 'refunds' | 'reviews';

interface TileDef {
  id: string;
  label: string;
  source: Source;
  /** The screen that owns the list behind the tile (design §2c): no screen, no tile. */
  screen: AdminScreenKey;
  to: string | null;
  note?: string;
  pick: (d: unknown) => QueueCount | undefined;
}

/**
 * The ten queues of Flow A.2a. Each `to` lands on the existing list with the matching filter; the
 * target pages read these params (see DashboardV2 notes in the Spec 069 PR).
 */
export const NEEDS_ACTION_TILES: TileDef[] = [
  { id: 'garages-pending', label: 'Garages to review', source: 'garages', screen: 'garages', to: '/garages?status=PENDING',
    pick: (d) => (d as GaragesNeedsAction).garagesPending },
  { id: 'services-pending', label: 'Services to approve', source: 'garages', screen: 'service-approvals', to: '/service-approvals',
    pick: (d) => (d as GaragesNeedsAction).servicesPending },
  { id: 'offers-pending', label: 'Offers to approve', source: 'garages', screen: 'service-approvals',
    to: '/service-approvals?tab=offers&status=PENDING_APPROVAL', pick: (d) => (d as GaragesNeedsAction).offersPending },
  { id: 'change-requests', label: 'Change requests', source: 'garages', screen: 'change-requests', to: '/change-requests?status=PENDING',
    pick: (d) => (d as GaragesNeedsAction).changeRequests },
  { id: 'service-proposals', label: 'Service proposals', source: 'garages', screen: 'service-approvals', to: '/service-proposals?status=PENDING',
    pick: (d) => (d as GaragesNeedsAction).serviceProposals },
  // #1101 — counts garages with documents to review; the list shares the backend predicate.
  { id: 'documents-pending', label: 'Garage documents to review', source: 'garages', screen: 'garages',
    to: '/garages?status=DOCUMENTS_PENDING',
    pick: (d) => (d as GaragesNeedsAction).documentsPending },
  // The portal has no refunds list; pending refunds are approved from the order page.
  { id: 'refunds-pending', label: 'Refunds awaiting approval', source: 'refunds', screen: 'orders', to: null,
    note: 'Approve from the order page', pick: (d) => (d as RefundsDashboard).pendingApproval },
  { id: 'orders-needs-action', label: 'Orders needing action', source: 'orders', screen: 'orders', to: '/orders?tab=NEEDS_ACTION',
    pick: (d) => (d as OrdersDashboard).needsAction },
  { id: 'orders-disputed', label: 'Disputed orders', source: 'orders', screen: 'orders', to: '/orders?tab=DISPUTED',
    pick: (d) => (d as OrdersDashboard).disputed },
  { id: 'reported-reviews', label: 'Reported reviews', source: 'reviews', screen: 'reported-reviews', to: '/reported-reviews?status=PENDING',
    pick: (d) => (d as ReviewsDashboard).reportedPending },
];

const SOURCE_TITLE: Record<Source, string> = {
  garages: 'Garage and service queues',
  orders: 'Order queues',
  refunds: 'Refund queue',
  reviews: 'Review queue',
};

export function sourceNeeded(source: Source, hasScreen: (s: string) => boolean): boolean {
  return NEEDS_ACTION_TILES.some((t) => t.source === source && hasScreen(t.screen));
}

interface NeedsActionBoardProps {
  blocks: Record<Source, DashboardBlock<unknown>>;
  hasScreen: (screen: string) => boolean;
  now: Date;
}

export default function NeedsActionBoard({ blocks, hasScreen, now }: NeedsActionBoardProps) {
  const visible = NEEDS_ACTION_TILES.filter((t) => hasScreen(t.screen));
  if (visible.length === 0) return null;

  const failed = (Object.keys(blocks) as Source[]).filter(
    (s) => blocks[s].status === 'error' && visible.some((t) => t.source === s),
  );

  return (
    <section className="dash-section" aria-labelledby="needs-action-title">
      <h3 id="needs-action-title" className="dash-section__title">Needs action</h3>
      <div className="needs-action-grid">
        {visible.map((t) => {
          const block = blocks[t.source];
          if (block.status === 'loading' || block.status === 'idle') {
            return <div key={t.id} className="needs-action-tile needs-action-tile--loading" aria-hidden="true"><span className="needs-action-tile__label">{t.label}</span><span className="needs-action-tile__count">…</span></div>;
          }
          if (block.status !== 'ok' || !block.data) return null;
          const q = t.pick(block.data);
          if (!q) return null;
          return <NeedsActionTile key={t.id} id={t.id} label={t.label} count={q.count ?? 0} oldestAt={q.oldestAt ?? null} to={t.to} note={t.note} now={now} />;
        })}
        {failed.map((s) => (
          <BlockErrorState key={`err-${s}`} title={SOURCE_TITLE[s]} message={blocks[s].error} onRetry={blocks[s].retry} />
        ))}
      </div>
    </section>
  );
}
